import React from "react";
import ReactPaginate from "react-paginate";
import style from "./Pagination.module.scss";
import useDomainStore from "../../stores/domain";
import { useMediaQuery } from "../../hooks/useMediaQuery";

/**
 * A reusable pagination component that displays page numbers and item count information.
 * 
 * @component
 * @param {Object} props - The component props
 * @param {number} props.pageCount - Total number of pages
 * @param {Function} props.onPageChange - Callback function called when page is changed
 * @param {number} props.currentPage - Current active page (0-based index)
 * @param {number} [props.totalItems=null] - Total number of items across all pages
 * @param {number} [props.itemsPerPage=null] - Number of items displayed per page
 * @returns {JSX.Element} A pagination component with page navigation and item count display
 * 
 * @example
 * <Pagination
 *   pageCount={10}
 *   currentPage={0}
 *   onPageChange={({ selected }) => handlePageChange(selected)}
 *   totalItems={100}
 *   itemsPerPage={10}
 * />
 */
function Pagination({ pageCount, onPageChange, currentPage, totalItems = null, itemsPerPage = null }) {
  const { isKurvDomain } = useDomainStore();
  const start = currentPage * itemsPerPage + 1;
  const end = Math.min((currentPage + 1) * itemsPerPage, totalItems);
  const isMobile = useMediaQuery(768);

  return (
    <div className={`${style.pagination} ${isKurvDomain ? style.kurv_theme : ""}`}>
      <ReactPaginate
        breakLabel="..."
        nextLabel={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M11.3537 8.35391L6.35372 13.3539C6.30727 13.4004 6.25212 13.4372 6.19142 13.4624C6.13072 13.4875 6.06567 13.5004 5.99997 13.5004C5.93428 13.5004 5.86922 13.4875 5.80852 13.4624C5.74783 13.4372 5.69268 13.4004 5.64622 13.3539C5.59977 13.3075 5.56292 13.2523 5.53778 13.1916C5.51263 13.1309 5.49969 13.0659 5.49969 13.0002C5.49969 12.9345 5.51263 12.8694 5.53778 12.8087C5.56292 12.748 5.59977 12.6929 5.64622 12.6464L10.2931 8.00016L5.64622 3.35391C5.5524 3.26009 5.49969 3.13284 5.49969 3.00016C5.49969 2.86747 5.5524 2.74023 5.64622 2.64641C5.74004 2.55259 5.86729 2.49988 5.99997 2.49988C6.13265 2.49988 6.2599 2.55259 6.35372 2.64641L11.3537 7.64641C11.4002 7.69284 11.4371 7.74799 11.4623 7.80869C11.4874 7.86938 11.5004 7.93445 11.5004 8.00016C11.5004 8.06586 11.4874 8.13093 11.4623 8.19163C11.4371 8.25233 11.4002 8.30747 11.3537 8.35391Z"
              fill={isKurvDomain ? "#22A276" : "#3A383F"}
            />
          </svg>
        }
        onPageChange={onPageChange}
        pageCount={pageCount}
        forcePage={currentPage} // This will control the active page
        pageRangeDisplayed={isMobile ? 1 : 3} // Show only 4 page buttons at a time
        marginPagesDisplayed={ isMobile ? 0 : 1 } // Show 1 page at the beginning and end
        previousLabel={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M10.3538 3.35384C10.4002 3.30738 10.4371 3.25223 10.4622 3.19154C10.4874 3.13084 10.5003 3.06579 10.5003 3.00009C10.5003 2.93439 10.4874 2.86934 10.4622 2.80864C10.4371 2.74794 10.4002 2.69279 10.3538 2.64634C10.3073 2.59988 10.2522 2.56303 10.1915 2.53789C10.1308 2.51275 10.0657 2.49981 10 2.49981C9.93433 2.49981 9.86928 2.51275 9.80858 2.53789C9.74788 2.56303 9.69273 2.59988 9.64628 2.64634L4.64628 7.64634C4.59979 7.69277 4.56291 7.74792 4.53775 7.80862C4.51259 7.86932 4.49963 7.93438 4.49963 8.00009C4.49963 8.0658 4.51259 8.13086 4.53775 8.19156C4.56291 8.25226 4.59979 8.3074 4.64628 8.35384L9.64628 13.3538C9.7401 13.4477 9.86735 13.5004 10 13.5004C10.1327 13.5004 10.26 13.4477 10.3538 13.3538C10.4476 13.26 10.5003 13.1328 10.5003 13.0001C10.5003 12.8674 10.4476 12.7402 10.3538 12.6463L5.7069 8.00009L10.3538 3.35384Z"
              fill={isKurvDomain ? "#22A276" : "#3A383F"}
            />
          </svg>
        }
        renderOnZeroPageCount={null}
        activeClassName={style.selected}
        disabledClassName={style.disabled}
      />

      {totalItems && itemsPerPage && (
        <p className={style.total_description}>
          { isMobile ? `Show ${end} of ${totalItems}`
          : `Show ${start} to ${end} of ${totalItems}`
          }
        </p>
      )}
    </div>
  );
}

export default Pagination;
